import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import PremiumLoader from "../../components/PremiumLoader";
import { createBookingWithPayment } from "../../api/bookingApi.js";
import { registerPassenger } from "../../api/squareApi.js";

function Payment() {
    const [loading, setLoading] = useState(false);
    const [paymentError, setPaymentError] = useState("");
    const [isSomeoneElse, setIsSomeoneElse] = useState(false);

    const bookingState = useSelector((state) => state.booking);
    const { 
        fleet_record_with_rate_id, 
        maps_record_id, 
        quote_info_record_id, 
        selected_vehicle_id,
        booking_info_record_id,
        pickup,
        dropoff,
        date,
        time,
        fleet_selection
    } = bookingState;

    const navigate = useNavigate();

    const handleConfirm = async (e) => {
        e.preventDefault();
        setLoading(true);
        setPaymentError('');

        const formData = new FormData(e.target);
        const phone = formData.get("phone");

        try {
            setLoading(true);

            // Parse date "MM/DD/YYYY" and time "h:mmpm" to "YYYY-MM-DD HH:MM:00"
            let formattedDatetime = "";
            try {
                const [month, day, year] = date.split('/');
                const timeStr = time.toLowerCase().replace(/\s/g, '');
                const isPM = timeStr.includes('pm');
                const isAM = timeStr.includes('am');
                const cleanTime = timeStr.replace(/[ap]m/, '');
                let [hourStr, minStr] = cleanTime.split(':');
                let hour = parseInt(hourStr, 10);
                if (isPM && hour < 12) hour += 12;
                if (isAM && hour === 12) hour = 0;
                formattedDatetime = `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')} ${hour.toString().padStart(2, '0')}:${minStr.padStart(2, '0')}:00`;
            } catch (err) {
                console.error("Date formatting failed", err);
                formattedDatetime = `${date} ${time.replace(/:00$/, '')}:00`;
            }

            const passengerData = {
                name: formData.get("full-name"),
                email: formData.get("email"),
                phone: phone
            };

            const passengerResponse = await registerPassenger(passengerData);

            if (!passengerResponse || !passengerResponse.passenger_id) {
                throw new Error(passengerResponse?.msg || passengerResponse?.message || 'Passenger registration failed. Please check phone/email.');
            }

            const passengerId = passengerResponse.passenger_id;

            const confirmData = {
                passenger_id: passengerId,
                pickup_datetime: formattedDatetime,
                start: pickup?.address || pickup?.name || '',
                end: dropoff?.address || dropoff?.name || '',
                pickup: bookingState.pickup,
                dropoff: bookingState.dropoff,
                journey_date: `${date} ${time}`,
                fleet_id: bookingState.fleet_id,
                amount: "1.00", 
                service_type: bookingState.service_type || "point_to_point",
                passenger_first_name: formData.get("full-name")?.split(' ')[0] || '',
                passenger_last_name: formData.get("full-name")?.split(' ').slice(1).join(' ') || '',
                customer_email: formData.get("email"),
                customer_cell_no: phone,
                fleet_name: fleet_selection?.name || "Standard Car",
                fare_details: {
                    final_fare: fleet_selection?.fare_details?.final_fare || fleet_selection?.fare_details?.total_fare || 0,
                    selected_fleet_id: bookingState.fleet_id
                },
                quote_info_record_id: quote_info_record_id,
                maps_record_id: maps_record_id,
                fleet_record_with_rate_id: fleet_record_with_rate_id,
                selected_vehicle_id: selected_vehicle_id,
                booking_info_record_id: booking_info_record_id,
                flight_name: formData.get("airline") || formData.get("flight-name") || "",
                flight_number: formData.get("flight_number") || formData.get("flight-number") || "",
                special_requirements: formData.get("special_instructions") || "",
                passengers: bookingState.passenger || 1,
                luggage: bookingState.luggage || 1,
                domain_id: bookingState.domain_id || 1,
                infant_seat: bookingState.infant || 0,
                regular_seat: bookingState.regular || 0,
                booster_seat: bookingState.booster || 0,
                is_someone_else: isSomeoneElse ? 1 : 0,
                rider_name: isSomeoneElse ? formData.get("rider_name") || "" : "",
                rider_cell_no: isSomeoneElse ? formData.get("rider_cell_no") || "" : "",
                rider_email: isSomeoneElse ? formData.get("rider_email") || "" : ""
            };

            const bookingResponse = await createBookingWithPayment(confirmData);

            if (!bookingResponse || !bookingResponse.booking_ref_id) {
                throw new Error('Booking creation failed - no booking reference received from dispatch system');
            }

            // Navigate to payment page with necessary details
            navigate('/payment', {
                state: {
                    bookingData: {
                        ...confirmData,
                        booking_ref_id: bookingResponse.booking_ref_id,
                        final_fare: fleet_selection?.fare_details?.final_fare || fleet_selection?.fare_details?.total_fare || 0
                    }
                }
            });

        } catch (error) {
            console.error("Booking error:", error);
            setPaymentError(error.message || "Reservation processing encountered an issue. Please review your details and try again.");
        } finally {
            setLoading(false);
        }
    };

    const getTotalFare = () => {
        if (!fleet_selection || !fleet_selection.fare_details) return '0.00';
        const total = parseFloat(fleet_selection.fare_details.final_fare || fleet_selection.fare_details.total_fare || fleet_selection.fare_details.journey_fare || 0);
        return total > 0 ? total.toFixed(2) : '0.00';
    };

    return (
        <main className="min-h-screen bg-[#0b132b] text-slate-100 font-body-main py-10 px-4 sm:px-6 lg:px-8">
            {loading && (
        <div className="fixed inset-0 z-50 bg-slate-950/80 backdrop-blur-md flex flex-col items-center justify-center">
          <PremiumLoader />
          <p className="mt-4 text-xs font-bold uppercase tracking-widest text-blue-400">Confirming Reservation & Securing Rates...</p>
        </div>
      )}
            
            <div className="max-w-7xl mx-auto">
                {/* Header Banner */}
                <div className="mb-8 pb-4 border-b border-slate-800 flex flex-col sm:flex-row sm:items-end justify-between gap-4">
                    <div>
                        <span className="text-xs font-bold uppercase tracking-widest text-blue-400 block mb-1">Step 3 of 3</span>
                        <h1 className="text-2xl sm:text-3xl font-extrabold text-white tracking-tight">Passenger & Flight Information</h1>
                        <p className="text-slate-400 text-xs sm:text-sm mt-1">Please provide passenger contact details and optional flight details for live tracking.</p>
                    </div>
                    <div className="text-xs text-slate-300 flex items-center gap-2 bg-slate-900/90 border border-slate-700/80 px-3.5 py-1.5 rounded-full shadow-sm">
                        <span className="w-2 h-2 rounded-full bg-blue-400 animate-pulse" />
                        <span>Corporate Booking Portal</span>
                    </div>
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-start">
                    
                    {/* Left Form: Passenger, Flight & Special Requests */}
                    <div className="lg:col-span-8 space-y-6">
                        <form id="booking-details-form" onSubmit={handleConfirm} className="space-y-6">
                            
                            {/* Passenger Details Card */}
                            <section className="rounded-2xl bg-slate-900/80 border border-slate-700/70 p-6 sm:p-8 backdrop-blur-xl shadow-xl">
                                <div className="flex items-center gap-3 mb-6 pb-4 border-b border-slate-800">
                                    <div className="w-10 h-10 rounded-xl bg-blue-600/20 text-blue-400 border border-blue-500/30 flex items-center justify-center">
                                        <span className="material-symbols-outlined !text-2xl">person</span>
                                    </div>
                                    <div>
                                        <h2 className="text-lg font-bold text-white tracking-tight">Passenger Contact Information</h2>
                                        <p className="text-xs text-slate-400">Driver updates & dispatch confirmations will be sent to these coordinates.</p>
                                    </div>
                                </div>

                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                                    <div className="sm:col-span-2">
                                        <label className="block text-xs font-bold text-slate-300 uppercase tracking-wider mb-1.5">
                                            Full Name *
                                        </label>
                                        <input 
                                            name="full-name" 
                                            type="text" 
                                            required 
                                            className="w-full bg-slate-950/80 border border-slate-700/80 rounded-xl px-4 py-3 text-sm text-white placeholder:text-slate-500 focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 outline-none transition-all" 
                                            placeholder="e.g. Jonathan Vance" 
                                        />
                                    </div>

                                    <div>
                                        <label className="block text-xs font-bold text-slate-300 uppercase tracking-wider mb-1.5">
                                            Corporate / Personal Email *
                                        </label>
                                        <input 
                                            name="email" 
                                            type="email" 
                                            required 
                                            className="w-full bg-slate-950/80 border border-slate-700/80 rounded-xl px-4 py-3 text-sm text-white placeholder:text-slate-500 focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 outline-none transition-all" 
                                            placeholder="jonathan@enterprise.com" 
                                        />
                                    </div>

                                    <div>
                                        <label className="block text-xs font-bold text-slate-300 uppercase tracking-wider mb-1.5">
                                            Mobile Phone Number *
                                        </label>
                                        <input 
                                            name="phone" 
                                            type="tel" 
                                            required 
                                            className="w-full bg-slate-950/80 border border-slate-700/80 rounded-xl px-4 py-3 text-sm text-white placeholder:text-slate-500 focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 outline-none transition-all" 
                                            placeholder="e.g. (617) 555-0199" 
                                        />
                                    </div>

                                    {/* Booking For Someone Else Toggle */}
                                    <div className="sm:col-span-2 pt-2 border-t border-slate-800">
                                        <label className="flex items-start gap-3 p-3.5 rounded-xl border border-slate-700/60 bg-slate-950/50 hover:border-amber-400/50 transition-all cursor-pointer group">
                                            <input 
                                                type="checkbox" 
                                                name="is_someone_else" 
                                                checked={isSomeoneElse}
                                                onChange={(e) => setIsSomeoneElse(e.target.checked)}
                                                className="w-4 h-4 mt-0.5 rounded text-amber-500 focus:ring-amber-500 bg-slate-900 border-slate-700 cursor-pointer"
                                            />
                                            <div className="flex flex-col">
                                                <span className="text-xs font-bold text-slate-200 group-hover:text-amber-400 transition-colors flex items-center gap-1.5">
                                                    <span className="material-symbols-outlined !text-[18px] text-amber-400">hail</span>
                                                    Booking for someone else? (Passenger / Rider is different from booker)
                                                </span>
                                                <span className="text-[11px] text-slate-400 mt-0.5">
                                                    Check this box if someone else will be traveling. Chauffeur will directly coordinate with the traveling rider.
                                                </span>
                                            </div>
                                        </label>
                                    </div>

                                    {/* Rider / Passenger Inputs */}
                                    {isSomeoneElse && (
                                        <div className="sm:col-span-2 rounded-xl bg-amber-500/5 border border-amber-500/20 p-5 space-y-4 animate-in fade-in duration-200">
                                            <div className="flex items-center gap-2 text-amber-400 font-bold text-xs uppercase tracking-wider border-b border-amber-500/20 pb-2">
                                                <span className="material-symbols-outlined !text-[18px]">badge</span>
                                                <span>Traveling Passenger (Rider) Details</span>
                                            </div>
                                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                                <div className="sm:col-span-2">
                                                    <label className="block text-xs font-bold text-slate-300 uppercase tracking-wider mb-1.5">
                                                        Rider Full Name *
                                                    </label>
                                                    <input 
                                                        name="rider_name" 
                                                        type="text" 
                                                        required={isSomeoneElse}
                                                        className="w-full bg-slate-950/90 border border-slate-700 rounded-xl px-4 py-2.5 text-sm text-white placeholder:text-slate-500 focus:border-amber-400 focus:ring-2 focus:ring-amber-400/20 outline-none transition-all" 
                                                        placeholder="e.g. Sarah Jenkins" 
                                                    />
                                                </div>
                                                <div>
                                                    <label className="block text-xs font-bold text-slate-300 uppercase tracking-wider mb-1.5">
                                                        Rider Mobile / Cell No. *
                                                    </label>
                                                    <input 
                                                        name="rider_cell_no" 
                                                        type="tel" 
                                                        required={isSomeoneElse}
                                                        className="w-full bg-slate-950/90 border border-slate-700 rounded-xl px-4 py-2.5 text-sm text-white placeholder:text-slate-500 focus:border-amber-400 focus:ring-2 focus:ring-amber-400/20 outline-none transition-all" 
                                                        placeholder="e.g. +1 617 555 0199" 
                                                    />
                                                    <span className="text-[10px] text-slate-400 mt-1 block">Primary number for chauffeur calls & SMS at pickup.</span>
                                                </div>
                                                <div>
                                                    <label className="block text-xs font-bold text-slate-300 uppercase tracking-wider mb-1.5">
                                                        Rider Email Address
                                                    </label>
                                                    <input 
                                                        name="rider_email" 
                                                        type="email" 
                                                        className="w-full bg-slate-950/90 border border-slate-700 rounded-xl px-4 py-2.5 text-sm text-white placeholder:text-slate-500 focus:border-amber-400 focus:ring-2 focus:ring-amber-400/20 outline-none transition-all" 
                                                        placeholder="e.g. rider@example.com" 
                                                    />
                                                    <span className="text-[10px] text-slate-400 mt-1 block">Live trip status & confirmation copy sent here.</span>
                                                </div>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            </section>

                            {/* Flight Radar Sync Card */}
                            <section className="rounded-2xl bg-slate-900/80 border border-slate-700/70 p-6 sm:p-8 backdrop-blur-xl shadow-xl">
                                <div className="flex items-center gap-3 mb-6 pb-4 border-b border-slate-800">
                                    <div className="w-10 h-10 rounded-xl bg-sky-600/20 text-sky-400 border border-sky-500/30 flex items-center justify-center">
                                        <span className="material-symbols-outlined !text-2xl">flight</span>
                                    </div>
                                    <div>
                                        <div className="flex items-center gap-2">
                                            <h2 className="text-lg font-bold text-white tracking-tight">Flight Tracking & Coordination</h2>
                                            <span className="text-[10px] uppercase font-bold text-emerald-300 bg-emerald-500/20 border border-emerald-500/30 px-2 py-0.5 rounded">
                                                Free Delay Wait
                                            </span>
                                        </div>
                                        <p className="text-xs text-slate-400">Our dispatchers monitor radar telemetry in real-time. If your flight is delayed, chauffeur schedule updates automatically.</p>
                                    </div>
                                </div>

                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                                    <div>
                                        <label className="block text-xs font-bold text-slate-300 uppercase tracking-wider mb-1.5">
                                            Airline Name (Optional)
                                        </label>
                                        <input 
                                            name="airline" 
                                            type="text" 
                                            className="w-full bg-slate-950/80 border border-slate-700/80 rounded-xl px-4 py-3 text-sm text-white placeholder:text-slate-500 focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 outline-none transition-all" 
                                            placeholder="e.g. Delta, United, American, JetBlue" 
                                        />
                                    </div>

                                    <div>
                                        <label className="block text-xs font-bold text-slate-300 uppercase tracking-wider mb-1.5">
                                            Flight Number (Optional)
                                        </label>
                                        <input 
                                            name="flight_number" 
                                            type="text" 
                                            className="w-full bg-slate-950/80 border border-slate-700/80 rounded-xl px-4 py-3 text-sm text-white placeholder:text-slate-500 focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 outline-none transition-all font-mono" 
                                            placeholder="e.g. DL 1492 / UA 224" 
                                        />
                                    </div>
                                </div>
                            </section>

                            {/* Chauffeur Instructions Card */}
                            <section className="rounded-2xl bg-slate-900/80 border border-slate-700/70 p-6 sm:p-8 backdrop-blur-xl shadow-xl">
                                <div className="flex items-center gap-3 mb-5 pb-4 border-b border-slate-800">
                                    <div className="w-10 h-10 rounded-xl bg-slate-800 text-slate-300 border border-slate-700 flex items-center justify-center">
                                        <span className="material-symbols-outlined !text-2xl">comment</span>
                                    </div>
                                    <div>
                                        <h2 className="text-lg font-bold text-white tracking-tight">Special Chauffeur Instructions</h2>
                                        <p className="text-xs text-slate-400">Terminal door preferences, child seats, heavy luggage assistance or gate meeting notes.</p>
                                    </div>
                                </div>

                                <textarea 
                                    name="special_instructions" 
                                    className="w-full bg-slate-950/80 border border-slate-700/80 rounded-xl px-4 py-3 text-sm text-white placeholder:text-slate-500 focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 outline-none transition-all" 
                                    placeholder="Enter terminal gate, booster seat request, luggage dimensions, meeting instructions..." 
                                    rows="3"
                                />
                            </section>
                            
                            {paymentError && (
                                <div className="p-4 rounded-xl bg-rose-500/15 border border-rose-500/30 text-rose-300 text-sm flex items-center gap-2">
                                    <span className="material-symbols-outlined text-rose-400">error</span>
                                    <span>{paymentError}</span>
                                </div>
                            )}
                        </form>
                    </div>

                    {/* Right: Executive Booking Recap Ticket */}
                    <div className="lg:col-span-4 lg:sticky lg:top-28">
                        <div className="rounded-2xl bg-slate-900/90 border border-slate-700/80 p-6 backdrop-blur-2xl shadow-2xl">
                            
                            <div className="flex items-center justify-between pb-4 border-b border-slate-800 mb-5">
                                <h3 className="font-bold text-white text-base flex items-center gap-2">
                                    <span className="material-symbols-outlined text-blue-400 !text-xl">receipt_long</span>
                                    Reservation Summary
                                </h3>
                                <span className="text-[11px] font-semibold text-blue-300 bg-blue-500/15 border border-blue-500/30 px-2 py-0.5 rounded-full">
                                    Confirmed Fare
                                </span>
                            </div>

                            {/* Selected Vehicle Showcase */}
                            <div className="p-4 rounded-xl bg-slate-950/80 border border-slate-800 mb-5 flex items-center gap-4">
                                <div className="w-20 h-14 rounded-lg bg-slate-900 p-1 flex items-center justify-center border border-slate-800">
                                    <img 
                                        alt="Vehicle" 
                                        className="max-h-full max-w-full object-contain filter drop-shadow" 
                                        src={fleet_selection?.info?.img_link || 'https://dispatch.bostonairportexpresscar.com/assets/images/fleet/1.png'} 
                                    />
                                </div>
                                <div>
                                    <span className="text-[10px] uppercase font-bold tracking-wider text-blue-400">Selected Class</span>
                                    <h4 className="font-bold text-white text-sm">{fleet_selection?.name || "Executive Chauffeur"}</h4>
                                    <p className="text-[11px] text-slate-400">
                                      {bookingState.passenger || 1} Guests • {bookingState.luggage || 0} Luggage
                                      {(Number(bookingState.booster) > 0 || Number(bookingState.regular) > 0 || Number(bookingState.infant) > 0) && (
                                        <span className="block text-emerald-400 text-[10px] font-medium mt-0.5">
                                          Child Seats: {[
                                            Number(bookingState.booster) > 0 ? `${bookingState.booster} Booster` : null,
                                            Number(bookingState.regular) > 0 ? `${bookingState.regular} Regular` : null,
                                            Number(bookingState.infant) > 0 ? `${bookingState.infant} Infant` : null
                                          ].filter(Boolean).join(' • ')}
                                        </span>
                                      )}
                                    </p>
                                </div>
                            </div>
                            
                            {/* Route Details */}
                            <div className="space-y-4 text-xs mb-6 pl-1">
                                <div className="flex items-start gap-2.5">
                                    <span className="w-2 h-2 rounded-full bg-blue-400 mt-1.5 shrink-0" />
                                    <div>
                                        <p className="text-[10px] uppercase font-bold text-slate-400">Pickup Location</p>
                                        <p className="font-medium text-slate-200 line-clamp-2 mt-0.5">{pickup?.address || 'N/A'}</p>
                                    </div>
                                </div>
                                <div className="flex items-start gap-2.5">
                                    <span className="w-2 h-2 rounded-full bg-sky-400 mt-1.5 shrink-0" />
                                    <div>
                                        <p className="text-[10px] uppercase font-bold text-slate-400">Dropoff Location</p>
                                        <p className="font-medium text-slate-200 line-clamp-2 mt-0.5">{dropoff?.address || 'N/A'}</p>
                                    </div>
                                </div>
                                <div className="flex items-center justify-between pt-2 border-t border-slate-800">
                                    <div>
                                        <p className="text-[10px] uppercase font-bold text-slate-400">Pickup Schedule</p>
                                        <p className="font-medium text-slate-200 mt-0.5">{date} • {time}</p>
                                    </div>
                                    <div className="text-right">
                                        <p className="text-[10px] uppercase font-bold text-slate-400">Est. Distance</p>
                                        <p className="font-medium text-slate-200 mt-0.5">{bookingState.distance || '--'}</p>
                                    </div>
                                </div>
                            </div>
                            
                            {/* Total Fare Breakdown */}
                            <div className="pt-4 border-t border-slate-800 space-y-2.5 text-xs">
                                <div className="flex justify-between text-slate-400">
                                    <span>Tolls, Logan Airport & Tunnel</span>
                                    <span className="text-blue-300 font-medium">Included</span>
                                </div>
                                <div className="flex justify-between text-slate-400">
                                    <span>Flight Delay Protection</span>
                                    <span className="text-emerald-400 font-medium">Complimentary</span>
                                </div>

                                {/* Dual Pricing Snapshot */}
                                <div className="grid grid-cols-2 gap-2 pt-2 border-t border-slate-800/80">
                                    <div className="p-2.5 rounded-lg bg-emerald-950/30 border border-emerald-500/20">
                                        <span className="text-[10px] uppercase font-bold text-emerald-400 block">By Cash</span>
                                        <span className="text-lg font-black text-emerald-200 font-mono">
                                            ${(parseFloat(getTotalFare()) * 0.90).toFixed(2)}
                                        </span>
                                        <p className="text-[9px] text-emerald-400/80">10% Cash Discount</p>
                                    </div>
                                    <div className="p-2.5 rounded-lg bg-blue-950/30 border border-blue-500/20">
                                        <span className="text-[10px] uppercase font-bold text-blue-400 block">By Card</span>
                                        <span className="text-lg font-black text-blue-200 font-mono">
                                            ${getTotalFare()}
                                        </span>
                                        <p className="text-[9px] text-blue-400/80">Standard Fare</p>
                                    </div>
                                </div>
                                
                                <div className="flex justify-between items-end pt-3 border-t border-slate-800 mt-2">
                                    <div>
                                        <span className="text-xs uppercase font-bold tracking-wider text-slate-300">Standard Card Fare</span>
                                        <p className="text-[10px] text-slate-500">All fees & taxes included</p>
                                    </div>
                                    <span className="text-3xl font-black text-white font-mono">${getTotalFare()}</span>
                                </div>
                            </div>
                            
                            {/* Submit Button */}
                            <button 
                                form="booking-details-form" 
                                type="submit" 
                                disabled={loading} 
                                className="w-full mt-6 py-4 rounded-xl exec-btn-primary flex items-center justify-center gap-2 text-xs uppercase tracking-wider font-extrabold shadow-lg"
                            >
                                Proceed to Payment & Confirmation
                                <span className="material-symbols-outlined !text-base">arrow_forward</span>
                            </button>
                            
                            <div className="mt-4 flex items-center justify-center gap-2 text-[11px] text-slate-400 text-center">
                                <span className="material-symbols-outlined text-emerald-400 !text-sm">lock</span>
                                <span>100% Secure & Encrypted Reservation</span>
                            </div>
                        </div>
                    </div>

                </div>
            </div>
        </main>
    );
}

export default Payment;
